import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { memo, useCallback } from "react";

import {
  useAdProfile,
  useAdUnitPath,
  useUserRoleTarget,
} from "~/components/advertisement/ad-slots/hooks";
import type { OutstreamAdUnit, Targeting } from "~/components/advertisement/ad-slots/types";
import * as gpt from "~/components/advertisement/google-publisher-tag/hooks";
import * as ix from "~/components/advertisement/index-exchange-wrapper/hooks";
import * as prebid from "~/components/advertisement/prebid/hooks";

export type Props = {
  adUnit: OutstreamAdUnit;
  className?: string;
  targeting?: Targeting;
  zone?: string | null;
};
const Component: FunctionComponent<Props> = ({ adUnit, className, targeting, zone }) => {
  const adUnitPath = useAdUnitPath(adUnit, zone);
  const userRoleTarget = useUserRoleTarget();
  const { adProfile } = useAdProfile();
  const outstreamSizeMappingBuilder = useCallback(
    (builder: googletag.SizeMappingBuilder) =>
      builder
        .addSize(
          [theme.breakpoints.values.xLargeDesktop, 0],
          [[1, 1], "fluid", [640, 360], [640, 480]],
        )
        .addSize(
          [theme.breakpoints.values.mediumDesktop, 0],
          [[1, 1], "fluid", [480, 360], [480, 270]],
        )
        .addSize([theme.breakpoints.values.desktop, 0], [[1, 1], "fluid", [320, 180]])
        .addSize([theme.breakpoints.values.tablet, 0], [[1, 1], "fluid", [480, 360], [480, 270]])
        .addSize([0, 0], [[1, 1], "fluid", [320, 180]])
        .build(),
    [],
  );

  const slot = gpt.useDefineSlot(
    adUnitPath,
    [[1, 1], "fluid", [640, 480], [480, 360], [640, 360], [320, 180]],
    {
      ...targeting,
      ...userRoleTarget,
      adProfile,
      ...ix.useTargeting(adUnit),
    },
    outstreamSizeMappingBuilder,
  );

  prebid.useRenderSlot(slot);

  return <div className={className} id={slot?.getSlotElementId()} />;
};

Component.displayName = "Slot";
export const Slot = memo(Component);
